// The rendered view of a document as HTML strings — one implementation of every
// block's markup, used by the <instruction-md> element (inside its shadow root),
// by SSR, and by the editor's preview (which draws the same markup with the
// same stylesheet, scoped; see styles.ts `scopedStyles`). Pure: no Lit, no DOM.
//
// THE LOOK is the component vocabulary people already read in technical UIs
// (shadcn/ui): hairline borders, 8px radius, muted secondary text, small
// medium-weight badges, one outline icon per status. Blocks differ by which
// component they are, not by decoration:
//   rules        one bordered list of clauses — status icon, the word, `R1 · name`
//                (the clause number is a CSS counter); the icon is the modality:
//                alert circle MUST · chevron circle SHOULD · minus circle
//                SHOULD NOT · ban NEVER · check circle MAY
//   guardrail    a destructive alert (shield)
//   alerts       an alert: icon, title, description
//   examples     a card with a Do / Don't badge
//   output       a dashed card with the format as a badge
//   every other  blocks.ts: a look per family — guidance groups, contracts, connected
//   kind         objects, media, technical declarations, layout, composition, extensions
//   figures      drawings (```svg), diagrams (```mermaid) and a workflow's steps,
//                drawn (figures.ts); the source stays what the agent receives
//
// Every text value is escaped here; `html` fields are the prose step's output,
// which never carries raw HTML from the source (prose.ts).
import type { Node } from "./project";
import { ICON_PATHS, LUCIDE, sentence } from "./icons";
import { escapeHtml as e } from "./md";
import { blockHtml } from "./blocks";

export const icon = (name: string): string => LUCIDE[name]
  ? `<svg class="lu" viewBox="0 0 24 24" aria-hidden="true">${LUCIDE[name]}</svg>`
  : `<svg viewBox="0 0 16 16" aria-hidden="true"><path d="${ICON_PATHS[name] ?? ICON_PATHS.core}"/></svg>`;

const RULES = new Set(["must", "always", "should", "avoid", "never", "may"]);
const idAttr = (id?: string) => (id ? ` id="${e(id)}"` : "");
const because = (h?: string) => (h ? `<div part="reason" class="because"><b>Because</b> ${h}</div>` : "");

/** A rule's modality: which status icon it carries, and the tone it is drawn in. */
function modality(n: Extract<Node, { type: "callout" }>): string {
  const negated = n.kind === "avoid" || / NOT$/.test(n.label);
  if (n.kind === "never" || (n.kind === "must" && negated)) return "never";
  if (negated) return "avoid";
  return n.kind === "always" ? "must" : n.kind;
}

function rule(n: Extract<Node, { type: "callout" }>): string {
  const m = modality(n);
  const meta = (n.condition ? `<span part="condition" class="badge">if ${e(n.condition)}</span>` : "") + (n.title ? `<span class="t">${e(n.title)}</span>` : "");
  return `<section part="callout" class="rule tone-${m}"${idAttr(n.id)} data-kind="${e(n.kind)}">`
    + `<div class="gut"><span part="label" class="lbl">${icon(m)}${e(sentence(n.label))}</span><span class="cn"${n.name ? ` title="${e(n.name)}"` : ""}>${n.name ? `<span class="nm">${e(n.name)}</span>` : ""}</span></div>`
    + `<div class="main">${meta ? `<div class="meta">${meta}</div>` : ""}<div part="body" class="body">${n.html}</div>${because(n.becauseHtml)}</div></section>`;
}

function callout(n: Extract<Node, { type: "callout" }>): string {
  const k = e(n.kind);
  if (RULES.has(n.kind)) return rule(n);
  if (n.kind === "example")
    return `<figure part="callout" class="card ex${n.avoid ? " avoid" : ""}"${idAttr(n.id)} data-kind="example"><figcaption class="ch"><span part="label" class="badge ${n.avoid ? "bad" : "good"}">${icon(n.avoid ? "never" : "must")}${n.avoid ? "Don’t" : "Do"}</span>${n.title ? `<span class="t">${e(n.title)}</span>` : ""}</figcaption><div part="body" class="body">${n.html}</div></figure>`;
  if (n.kind === "output")
    return `<section part="callout" class="card out"${idAttr(n.id)} data-kind="output"><div class="ch"><span part="label" class="t">${n.title ? e(n.title) : "Output"}</span><span class="sub">response shape</span>${n.format ? `<span part="format" class="badge mono">${e(n.format)}</span>` : ""}</div><div part="body" class="body">${n.html}</div>${n.schema ? `<div part="schema" class="sub">Schema <a class="imd-ref" part="ref" href="${e(n.schema.startsWith("#") ? n.schema : `#${n.schema.replace(/^@/, "")}`)}">${e(n.schema)}</a></div>` : ""}</section>`;
  const i = n.kind === "caution" ? "warning" : n.kind === "note" ? "info" : n.kind;
  return `<aside part="callout" class="alert tone-${k}"${idAttr(n.id)} data-kind="${k}">${icon(i)}<div><div part="label" class="at">${e(sentence(n.label))}${n.title ? ` — ${e(n.title)}` : ""}</div><div part="body" class="body">${n.html}</div>${because(n.becauseHtml)}</div></aside>`;
}

export function nodeHtml(n: Node): string {
  switch (n.type) {
    case "markdown":
      return `<div part="prose" class="prose">${n.html}</div>`;
    case "callout":
      return callout(n);
    case "block":
      return blockHtml(n, (nodes) => nodesHtml(nodes));
  }
}

/** Nodes in order; consecutive rules are one clause list. */
export function nodesHtml(nodes: Node[]): string {
  let out = "";
  let run = "";
  for (const n of nodes) {
    if (n.type === "callout" && RULES.has(n.kind)) { run += rule(n); continue; }
    if (run) { out += `<div class="rules">${run}</div>`; run = ""; }
    out += nodeHtml(n);
  }
  return out + (run ? `<div class="rules">${run}</div>` : "");
}
